import React from 'react';

/**
 * HUD — Heads-up display overlay
 *
 * Renders on top of the Canvas. Pure React, only updates
 * when the engine pushes state via uiBridge (~10fps).
 */

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    pointerEvents: 'none',
    fontFamily: "'Orbitron', sans-serif",
    color: '#ffffff',
    zIndex: 10,
    padding: '12px 16px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  stat: {
    fontSize: '13px',
    letterSpacing: '1px',
    textShadow: '0 0 8px rgba(0, 255, 65, 0.6)',
  },
  score: {
    fontSize: '20px',
    fontWeight: 900,
    textShadow: '0 0 12px rgba(0, 255, 65, 0.8)',
    color: '#00ff41',
  },
  combo: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#ffcc00',
    textShadow: '0 0 10px rgba(255, 204, 0, 0.6)',
  },
  hpBarContainer: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
    right: '16px',
    height: '6px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '3px',
    overflow: 'hidden',
    zIndex: 10,
    pointerEvents: 'none',
  },
  hpBar: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.15s ease-out, background-color 0.3s',
  },
  xpBarContainer: {
    position: 'absolute',
    bottom: '28px',
    left: '16px',
    right: '16px',
    height: '3px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '2px',
    overflow: 'hidden',
    zIndex: 10,
    pointerEvents: 'none',
  },
  xpBar: {
    height: '100%',
    background: '#a855f7',
    borderRadius: '2px',
    transition: 'width 0.2s ease-out',
    boxShadow: '0 0 6px rgba(168, 85, 247, 0.5)',
  },
  wave: {
    fontSize: '11px',
    color: '#00d4ff',
    textShadow: '0 0 8px rgba(0, 212, 255, 0.5)',
  },
  level: {
    fontSize: '12px',
    color: '#a855f7',
    textShadow: '0 0 8px rgba(168, 85, 247, 0.5)',
  },
};

export default function HUD({ score, kills, combo, level, xp, xpToNext, hp, maxHp, wave, elapsed }) {
  const hpPercent = maxHp > 0 ? (hp / maxHp) * 100 : 0;
  const xpPercent = xpToNext > 0 ? (xp / xpToNext) * 100 : 0;
  const hpColor = hpPercent > 60 ? '#00ff41' : hpPercent > 30 ? '#ffcc00' : '#ff0040';
  const minutes = Math.floor((elapsed || 0) / 60);
  const seconds = Math.floor((elapsed || 0) % 60);
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <div>
            <div style={styles.score}>{(score || 0).toLocaleString()}</div>
            <div style={styles.stat}>KILLS {kills || 0}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            {combo > 1 && (
              <div style={styles.combo}>x{combo} COMBO</div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={styles.wave}>WAVE {wave || 1}</div>
            <div style={styles.level}>LVL {level || 1}</div>
            <div style={{ ...styles.stat, fontSize: '11px', color: '#888' }}>{timeStr}</div>
          </div>
        </div>
      </div>

      {/* XP bar */}
      <div style={styles.xpBarContainer}>
        <div style={{ ...styles.xpBar, width: `${xpPercent}%` }} />
      </div>

      {/* HP bar */}
      <div style={styles.hpBarContainer}>
        <div style={{
          ...styles.hpBar,
          width: `${hpPercent}%`,
          backgroundColor: hpColor,
          boxShadow: `0 0 8px ${hpColor}`,
        }} />
      </div>
    </>
  );
}
